/* global __initial_auth_token, __app_id */

import React, { useState, useEffect, useCallback } from 'react';
import { appTypography } from '../theme/appTypography';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, doc, serverTimestamp, writeBatch, setDoc, collection } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import ReactMarkdown from 'react-markdown';
// --- IMPORT AXIOS CONFIG TO FIX CSRF/419 ERRORS ---
import api from '../axiosConfig'; 

import {
    Box, Typography, Button, Stepper, Step, StepLabel, Paper, CircularProgress, Alert,
    MenuItem, TextField, Grid, Avatar,
    Backdrop, useMediaQuery, Collapse, ButtonBase
} from '@mui/material';

import { ThemeProvider, createTheme, styled, responsiveFontSizes } from '@mui/material/styles';
import {
    FlightTakeoff as PlaneIcon,
    Home as HomeIcon,
    DirectionsCar as CarIcon,
    AssignmentInd as BriefcaseIcon,
    Edit as EditIcon,
    ReportProblem as AlertTriangleIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';

import LiveChatView from './LiveChatView';
import PhoneInputField, { COUNTRY_PHONE_CODES } from './PhoneInputField';

// --- Firebase Initialization ---
const firebaseConfig = {
    apiKey: "AIzaSyDArzbZ8gvcfBWhraTOtiEPrmdEAaTz5HY",
    authDomain: "multichannel-inquiry-system.firebaseapp.com",
    projectId: "multichannel-inquiry-system",
    storageBucket: "multichannel-inquiry-system.appspot.com",
    messagingSenderId: "240171385312",
    appId: "1:240171385312:web:16ba555d363cf85bc62b85",
    measurementId: "G-DF58M5DFXW"
};

let app;
if (!getApps().length) {
    app = initializeApp(firebaseConfig);
} else {
    app = getApp();
}

const firestoreDbInstance = getFirestore(app);
const firebaseAuthInstance = getAuth(app);

// --- Theme ---
let inquiryFlowTheme = createTheme({
    palette: {
        primary: { main: '#2db838', contrastText: '#fff', light: 'rgba(45, 184, 56, 0.1)' },
        secondary: { main: '#0288d1' },
        background: { default: '#f4f7f9', paper: '#FFFFFF' },
        text: { primary: '#3A3B3C', secondary: '#5A5A5A' },
        error: { main: '#d32f2f' },
        warning: { main: '#ffa000' },
        info: { main: '#0288d1', light: '#e1f5fe', dark: '#01579b' },
        success: { main: '#2e7d32', light: '#edf7ed', dark: '#1e4e24' }
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        h4: { fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2.0rem)', color: '#1A1A1A', marginBottom: '0.8rem', lineHeight: 1.2 },
        h5: { fontWeight: 600, fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', marginBottom: '0.8rem', color: '#2db838' },
        h6: { fontWeight: 500, fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', color: '#333' },
        subtitle1: { fontWeight: 400, fontSize: '0.9rem', color: '#6C757D', lineHeight: 1.5 },
        body1: { fontSize: '0.9rem', lineHeight: 1.5 },
        body2: { fontSize: '0.85rem', lineHeight: 1.5 },
        caption: { fontSize: '0.75rem', lineHeight: 1.4 },
        button: { textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '8px 18px', fontSize: '0.9rem' },
        ...appTypography,
    },
    shape: { borderRadius: 12 },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    backgroundImage: 'none'
                }
            }
        },
        MuiTextField: {
            defaultProps: { variant: 'outlined', size: 'small' },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                        '&:hover fieldset': { borderColor: '#2db838' },
                        '&.Mui-focused fieldset': { borderColor: '#259c30', borderWidth: '2px' },
                    },
                    '& .MuiInputLabel-outlined.Mui-focused': { color: '#259c30' },
                }
            }
        },
        MuiSelect: {
            defaultProps: { variant: 'outlined', size: 'small' },
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2db838' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#259c30', borderWidth: '2px' },
                },
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: { '& .MuiInputLabel-outlined.Mui-focused': { color: '#259c30' } }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 8 },
                containedPrimary: {
                    boxShadow: '0 3px 10px rgba(45, 184, 56, 0.25)',
                    '&:hover': { backgroundColor: '#259c30', boxShadow: '0 5px 14px rgba(45, 184, 56, 0.35)' },
                },
                outlinedPrimary: {
                    borderColor: '#2db838', color: '#2db838',
                    '&:hover': { backgroundColor: 'rgba(45, 184, 56, 0.06)', borderColor: '#259c30' }
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: { '&.Mui-active': { color: '#2db838' }, '&.Mui-completed': { color: '#259c30' }, transform: 'scale(0.9)'},
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: { '&.Mui-active': { fontWeight: 600 }, '&.Mui-completed': { fontWeight: 500 } }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: { borderRadius: 8, alignItems: 'center', padding: '8px 12px' },
                icon: { paddingTop: '0px', marginRight: '8px', fontSize: '1.2rem' },
                message: { padding: '2px 0' }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: { boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }
            }
        }
    },
});

inquiryFlowTheme = responsiveFontSizes(inquiryFlowTheme);

const steps = ['Select Inquiry Type', 'Provide Details', 'Live Chat'];

// --- PRIORITY & DATE HELPERS (NEW LOGIC) ---

const getRelevantTravelDate = (type, formData) => {
    if (!formData) return null;
    switch (type) {
        case 'travelInquiry': return formData.travelDate;
        case 'visaInquiry': return formData.travelDateVisa;
        case 'accommodationInquiry': return formData.checkInDate;
        case 'carHireInquiry': return formData.startDate;
        default: return null;
    }
};

const calculateLeadPriority = (dateString) => {
    // Default: Normal priority if no date provided
    if (!dateString) return { priority: 'normal', leadStatus: 'pipeline', isUrgent: false };

    const today = new Date();
    const travelDate = new Date(dateString);
    
    // Normalize to midnight for accurate day diff
    today.setHours(0,0,0,0);
    travelDate.setHours(0,0,0,0);

    const diffTime = travelDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // 1. URGENT (Today + 7 days)
    if (diffDays <= 7) {
        return { priority: 'high', leadStatus: 'urgent', isUrgent: true };
    }
    // 2. HOT LEAD (8 to 30 days)
    if (diffDays <= 30) {
        return { priority: 'medium', leadStatus: 'hot', isUrgent: false };
    }
    // 3. PIPELINE (> 30 days)
    return { priority: 'normal', leadStatus: 'pipeline', isUrgent: false };
};

// --- MAIN COMPONENT ---
export default function NewInquiryFlow() {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedInquiryType, setSelectedInquiryType] = useState(null);
    const [activeInquiryDetails, setActiveInquiryDetails] = useState(null);
    const [activeInquiryId, setActiveInquiryId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [firebaseInitError, setFirebaseInitError] = useState(null);
    const [globalError, setGlobalError] = useState('');
    const isMobile = useMediaQuery(inquiryFlowTheme.breakpoints.down('sm'));

    const appIdForPath = (typeof __app_id !== 'undefined' && __app_id) ? __app_id : 'default-app-id';

    useEffect(() => {
        if (!firestoreDbInstance || !firebaseAuthInstance) {
            setFirebaseInitError("System Error: Services not loaded. Please try again later.");
            setIsAuthReady(true);
            return;
        }
        setFirebaseInitError(null);

        const unsubscribe = onAuthStateChanged(firebaseAuthInstance, async (user) => {
            if (user) {
                setUserId(user.uid);
            } else {
                try {
                    if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
                        await signInWithCustomToken(firebaseAuthInstance, __initial_auth_token);
                    } else {
                        await signInAnonymously(firebaseAuthInstance);
                    }
                } catch (error) {
                    console.error("Error signing in:", error);
                    setFirebaseInitError(`Failed to initialize session: ${error.message}.`);
                    setUserId(`local_${Date.now()}`);
                }
            }
            setIsAuthReady(true);
        });
        return () => unsubscribe();
    }, []);

    const handleInquiryTypeSelect = (inquiryTypeKey) => {
        setSelectedInquiryType(inquiryTypeKey);
        setCurrentStep(1);
        setGlobalError('');
    };

    const handleInquirySubmitSuccess = async (inquiryDataFromForm) => {
        if (!userId) {
            setGlobalError("Authentication not ready. Please try again.");
            return;
        }
        setIsLoading(true);
        setGlobalError('');

        try {
            // --- CALCULATE PRIORITY ON SUBMISSION ---
            const relevantDate = getRelevantTravelDate(selectedInquiryType, inquiryDataFromForm);
            const { priority, leadStatus, isUrgent } = calculateLeadPriority(relevantDate);
            // ----------------------------------------

            const payload = {
                inquiryUserType: 'client',
                inquiryType: selectedInquiryType,
                formData: inquiryDataFromForm,
                // --- Send Flags to Backend API (For Email/SMS Triggers) ---
                priority,      // 'high', 'medium', 'normal'
                leadStatus,    // 'urgent', 'hot', 'pipeline'
                isUrgent,      // boolean
                travelDate: relevantDate // Standardized date for backend processing
            };

            // Use 'api' (Axios) to handle CSRF tokens and cookies automatically
            await api.post('/inquiries', payload);

            const timestamp = serverTimestamp();
            const welcomeMessage = `Welcome ${inquiryDataFromForm.fullName}! A support agent will be with you shortly.`;
            
            const sanitizedFormData = Object.fromEntries(
                Object.entries(inquiryDataFromForm).map(([key, value]) => [key, value === undefined ? null : value])
            );

            const newInquiryId = `${Date.now()}-${userId.substring(0, 5)}`;

            // --- PRESERVED DATA STRUCTURE + PRIORITY FIELDS ---
            const commonInquiryData = {
                type: selectedInquiryType,
                userId,
                appId: appIdForPath,
                status: 'new_chat',
                createdAt: timestamp,
                updatedAt: timestamp,
                originalInquiryId: newInquiryId,
                formData: {
                    ...sanitizedFormData,
                    travelDate: relevantDate // Ensure dashboard always finds the date
                },
                inquiryUserType: 'client',
                lastMessageBy: 'user',
                lastMessageSnippet: "Inquiry created.",
                consultantUnreadCount: 1,
                userUnreadCount: 0,
                // --- Save to Firebase for Instant Dashboard Sorting ---
                priority,
                leadStatus,
                isUrgent
            };
            // -----------------------------------------

            const batch = writeBatch(firestoreDbInstance);
            const userInquiryRef = doc(firestoreDbInstance, `artifacts/${appIdForPath}/users/${userId}/inquiries`, newInquiryId);
            batch.set(userInquiryRef, commonInquiryData);
            const globalInquiryDocRef = doc(firestoreDbInstance, "allInquiries", newInquiryId);
            batch.set(globalInquiryDocRef, commonInquiryData);
            const welcomeMessageRef = doc(collection(userInquiryRef, 'messages'));
            batch.set(welcomeMessageRef, {
                text: welcomeMessage,
                sender: 'system',
                senderType: 'system',
                timestamp: serverTimestamp(),
            });

            await batch.commit();

            setActiveInquiryId(newInquiryId);
            setActiveInquiryDetails({ ...commonInquiryData, createdAt: new Date() });
            setCurrentStep(2);

        } catch (error) {
            console.error("Error creating inquiry:", error);
            
            let errorMessage = error.message || "An unexpected error occurred. Please try again.";
            
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.message) errorMessage = data.message;
                if (data.errors) {
                    errorMessage = Object.values(data.errors).flat().join(' ');
                }
            }
            
            setGlobalError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => prev - 1);
        if (currentStep === 1) setSelectedInquiryType(null);
        setGlobalError('');
    };

    const handleEndChatAndReset = async () => {
        if (userId && firestoreDbInstance) {
            const userStatusRef = doc(firestoreDbInstance, `userPresence/${userId}`);
            try {
                await setDoc(userStatusRef, { isOnline: false, lastSeen: serverTimestamp(), isTypingTo: null }, { merge: true });
            } catch (err) { console.warn("Error setting user offline on chat end:", err); }
        }

        if (activeInquiryId && userId && appIdForPath && firestoreDbInstance) {
            const timestamp = serverTimestamp();
            const updateData = {
                status: 'closed_by_user',
                closedAt: timestamp,
                updatedAt: timestamp,
                lastMessageBy: 'user',
                lastMessageSnippet: 'Chat ended by user.'
            };
            try {
                const globalInquiryDocRef = doc(firestoreDbInstance, `allInquiries/${activeInquiryId}`);
                const userSpecificInquiryDocRef = doc(firestoreDbInstance, `artifacts/${appIdForPath}/users/${userId}/inquiries/${activeInquiryId}`);

                const batch = writeBatch(firestoreDbInstance);
                batch.update(globalInquiryDocRef, updateData);
                batch.update(userSpecificInquiryDocRef, updateData);
                await batch.commit();
            } catch (error) {
                console.error("Error updating inquiry to closed_by_user:", error);
                setGlobalError("Failed to properly end the chat session. Please try starting a new inquiry.");
                return;
            }
        }
        setCurrentStep(0);
        setSelectedInquiryType(null);
        setActiveInquiryId(null);
        setActiveInquiryDetails(null);
        setGlobalError('');
    };

    if (firebaseInitError) {
        return (
            <ThemeProvider theme={inquiryFlowTheme}>
                <Box sx={{display: 'flex', flexDirection:'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p:2, textAlign:'center', bgcolor: 'background.default'}}>
                    <Avatar sx={{bgcolor:'error.main', width:50, height:50, mb:1.5}}><AlertTriangleIcon/></Avatar>
                    <Typography variant="h5" color="error.dark" gutterBottom>System Initialization Error</Typography>
                    <Alert severity="error" sx={{textAlign:'left', mt: 1, maxWidth: 500}}>{firebaseInitError}</Alert>
                </Box>
            </ThemeProvider>
        );
    }

    if (!isAuthReady || !userId) {
        return (
            <ThemeProvider theme={inquiryFlowTheme}>
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection:'column', bgcolor: 'background.default'}}>
                    <CircularProgress size={40} />
                    <Typography sx={{ml:0, mt:1.5, color: 'text.secondary'}} variant="body1">Initializing your secure session...</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={inquiryFlowTheme}>
            <Box sx={{
                 flexGrow: 1,
                 display: 'flex',
                 flexDirection: 'column',
                 bgcolor: 'background.default',
                 height: ['100vh', '100dvh'],
                 p: 0,
                 overflow: 'hidden',
                 '@media (max-width:600px)': {
                 fontSize: '0.85rem',
                 lineHeight: 1.4,
                 }
                 }}>
                <Paper elevation={3} sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 0,
                    boxShadow: 'none',
                    border: 'none',
                }}>
                    <Box sx={{
                        width: '100%', p: {xs: 2, sm: 3}, display: 'flex', flexDirection: 'column',
                        flexGrow: 1, minHeight: 0, overflowY: 'auto'
                    }}>
                        <Stepper activeStep={currentStep} alternativeLabel={!isMobile} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: {xs:2, md:3}, flexShrink: 0, width: '100%' }}>
                            {steps.map((label) => (
                                <Step key={label}><StepLabel>{label}</StepLabel></Step>
                            ))}
                        </Stepper>

                        <Box sx={{
                            display:'flex', alignItems:'center', justifyContent: 'center',
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'center', sm: 'left' },
                            mb: 3, flexShrink: 0
                        }}>
                            <img src="./pics/logo.png" alt="Company Logo"
                                 style={{height: '36px', marginRight: isMobile ? '0' : '16px', marginBottom: isMobile ? '8px' : '0'}}
                                 onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/160x40/cccccc/FFFFFF?text=Logo+Error'; }}
                            />
                            <Typography variant="h4" component="h1" sx={{mb: 0}}>Client Inquiry Assistance</Typography>
                        </Box>

                        {globalError && <Alert severity="error" sx={{mb:2}} onClose={()=>setGlobalError('')}>{globalError}</Alert>}


                        <Box sx={{mt:2, position:'relative', flexGrow:1, display:'flex', flexDirection:'column', minHeight:0}}>
                            {currentStep === 0 && <SelectInquiryTypeStep onSelectType={handleInquiryTypeSelect} />}
                            {currentStep === 1 && selectedInquiryType && (
                                <InquiryDetailsStep
                                    inquiryType={selectedInquiryType} onSubmit={handleInquirySubmitSuccess}
                                    onBack={handleBack} isLoading={isLoading}
                                />
                            )}
                            {currentStep === 2 && activeInquiryId && activeInquiryDetails && (
                                <LiveChatView
                                    inquiryId={activeInquiryId} 
                                    inquiryDetails={activeInquiryDetails}
                                    userId={userId} 
                                    onEndChat={handleEndChatAndReset}
                                    db={firestoreDbInstance} 
                                    appId={appIdForPath}
                                />
                            )}
                            {isLoading && currentStep !== 2 && (
                                <Backdrop open={isLoading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, position: 'absolute', borderRadius: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                    <CircularProgress color="primary" />
                                </Backdrop>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </ThemeProvider>
    );
}

// --- SUB-COMPONENTS ---

const inquiryTypesData = [
    { key: 'travelInquiry', name: 'Travel Inquiry', icon: <PlaneIcon sx={{fontSize: {xs:28, sm:32}}} />, description: "Flights, tours, travel packages." },
    { key: 'visaInquiry', name: 'Visa Inquiry', icon: <BriefcaseIcon sx={{fontSize: {xs:28, sm:32}}} />, description: "Visa applications and requirements." },
    { key: 'accommodationInquiry', name: 'Accommodation', icon: <HomeIcon sx={{fontSize: {xs:28, sm:32}}} />, description: "Hotel bookings and lodging." },
    { key: 'carHireInquiry', name: 'Car Hire', icon: <CarIcon sx={{fontSize: {xs:28, sm:32}}} />, description: "Rental cars and transportation." },
];

const StyledInquiryCard = styled(ButtonBase)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0px 4px 15px -3px rgba(0,0,0,0.07)`,
    transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], { duration: theme.transitions.duration.short }),
    cursor: 'pointer',
    textAlign: 'left',
    justifyContent: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0px 10px 25px -5px ${theme.palette.primary.light}`,
        transform: 'translateY(-4px)'
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
        boxShadow: `0px 10px 25px -5px ${theme.palette.primary.light}`,
    },
}));

function SelectInquiryTypeStep({ onSelectType }) {
    const isMobile = useMediaQuery(inquiryFlowTheme.breakpoints.down('sm'));
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'center'}}>
            <Typography variant="h5" align="center" gutterBottom sx={{mb:1, color: 'text.primary'}}>How can we help you today?</Typography>
            <Typography variant="body1" align="center" sx={{mb:4, color:'text.secondary'}}>Select an inquiry type to connect with the right expert.</Typography>
            
            {/* FIX: Use MUI v6/Grid2 syntax (size prop) instead of item/xs */}
            <Grid container spacing={isMobile ? 2.5 : 4} justifyContent="center">
                {inquiryTypesData.map((type, index) => (
                    <Grid key={type.key} size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                        <Collapse in={true} timeout={300 + index * 150}>
                            {/* FIX: component="div" to prevent button-in-button error */}
                            <StyledInquiryCard onClick={() => onSelectType(type.key)} component="div">
                                <Avatar sx={{ bgcolor: 'primary.light', width: {xs: 48, sm: 56}, height: {xs: 48, sm: 56}, mr: 2, color:'primary.main' }}>{type.icon}</Avatar>
                                <Box>
                                    <Typography variant="h6" component="h3" sx={{fontWeight: 600, color: 'text.primary', mb:0.25}}>{type.name}</Typography>
                                    <Typography variant="body2" sx={{color: 'text.secondary'}}>{type.description}</Typography>
                                </Box>
                            </StyledInquiryCard>
                        </Collapse>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{mt: 'auto', pt: 2, width: '100%', alignSelf: 'center'}}>
                <Typography variant="body2" color="text.secondary" align="center" sx={{mb:2, fontStyle:'italic', display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <InfoIcon sx={{fontSize: 18, verticalAlign:'middle', marginRight: '6px', color:'primary.main'}}/>
                    For urgent matters, live chat offers the quickest response.
                </Typography>
                <Paper elevation={1} sx={{p:2, textAlign:'center', borderRadius:2, background: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]}}>
                    <Typography variant="subtitle1" sx={{mb:1, color: 'text.secondary'}}>Need to send a detailed message instead?</Typography>
                    <Button variant="outlined" color="secondary" size="small" startIcon={<EditIcon />}
                        href="https://www.abisiniya.com/contact-us" target="_blank" rel="noopener noreferrer"
                    >Send a Message</Button>
                </Paper>
            </Box>
        </Box>
    );
}

const BaseInquiryFormMui = ({ title, fields, onSubmit, onBack, isLoading, inquiryType, getCountryLabel }) => {
    const [formData, setFormData] = useState(() => {
        const initialFormState = {};
        fields.forEach(field => {
            initialFormState[field.name] = (field.type === 'checkbox' ? false : '');
        });
        // Seed the default country for the phone field
        initialFormState.phoneCountryCode = 'ZW'; // Zimbabwe default
        initialFormState.phoneDialCode    = '263';
        return initialFormState;
    });
    const [errors, setErrors] = useState({});
    const [currentPrompt, setCurrentPrompt] = useState('');
    const [suggestiveText, setSuggestiveText] = useState('');
    const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
    const [suggestionError, setSuggestionError] = useState('');
    const [showHelpfulInfoBox, setShowHelpfulInfoBox] = useState(false);

    useEffect(() => {
        const newPrompt = (() => {
            if (inquiryType === 'visaInquiry' && formData.nationality && formData.destinationCountryVisa) {
                const nationalityLabel = getCountryLabel(formData.nationality);
                const destinationLabel = getCountryLabel(formData.destinationCountryVisa);
                if (nationalityLabel && destinationLabel && nationalityLabel !== "Select Country" && destinationLabel !== "Select Country") {
                    return `Provide a concise summary of visa requirements for a ${nationalityLabel} citizen traveling to ${destinationLabel} for tourism.`;
                }
            } else if (inquiryType === 'travelInquiry' && formData.destination) {
                return `Offer brief travel tips for visiting ${formData.destination}.`;
            }
            return '';
        })();

        if (newPrompt && newPrompt !== currentPrompt) {
            setCurrentPrompt(newPrompt);
            setSuggestiveText('');
            setSuggestionError('');
            setShowHelpfulInfoBox(false);
        }
    }, [formData, inquiryType, getCountryLabel, currentPrompt]);

  const fetchSuggestiveDataFromAPI = useCallback(async (promptToFetch) => {
  if (!promptToFetch) return;

  setIsLoadingSuggestion(true);
  setSuggestiveText('');
  setSuggestionError('');
  setShowHelpfulInfoBox(true);

  const apiKey = "AIzaSyDkKtzeHtJ-5Z9pNCQrxzJcynSq5fzr2Ps";
  

const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          role: "user",
          parts: [{ text: promptToFetch }]
        }]
      })
    });

    if (response.status === 429) {
      throw new Error("Quota exceeded. Try again later.");
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Gemini API Error Details:", errorData);
      throw new Error(errorData?.error?.message || "Gemini API error");
    }

    const result = await response.json();
    const text = result?.candidates?.[0]?.content?.parts?.[0]?.text;

    if (text) {
      setSuggestiveText(text);
    } else {
      setSuggestionError("No suggestion returned.");
    }

  } catch (error) {
    console.error('Error fetching suggestive data:', error);
    setSuggestionError(error.message);
  } finally {
    setIsLoadingSuggestion(false);
  }
}, []);


    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const validate = useCallback(() => {
        let isValid = true;
        const newErrors = {};
        fields.forEach(field => {
            if (field.required && !formData[field.name]) {
                newErrors[field.name] = field.errorMessage || `${field.label} is required`;
                isValid = false;
            }
        });
        // Email format validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address (e.g., you@example.com)';
            isValid = false;
        }
        // Phone/WhatsApp — validate digit count using the selected country's rules
        if (formData.phone) {
            const country = COUNTRY_PHONE_CODES.find(c => c.code === formData.phoneCountryCode)
                         || COUNTRY_PHONE_CODES.find(c => c.dial === formData.phoneDialCode);
            if (country) {
                const localDigits = formData.phone.replace(/\D/g, '').slice(country.dial.length);
                if (localDigits.length < country.min || localDigits.length > country.max) {
                    const expected = country.min === country.max
                        ? `${country.min}`
                        : `${country.min}–${country.max}`;
                    newErrors.phone = `${country.name} (+${country.dial}) requires ${expected} digits — you entered ${localDigits.length}`;
                    isValid = false;
                }
            }
        }
        setErrors(newErrors);
        return isValid;
    }, [fields, formData]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
        }
    }, [validate, onSubmit, formData]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%', minHeight: 0 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>{title}</Typography>
            <Alert
                severity="warning"
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'warning.light',
                    bgcolor: '#fffbf0',
                    '& .MuiAlert-icon': { alignItems: 'center' },
                    '& .MuiAlert-message': { fontSize: '0.83rem', lineHeight: 1.6 }
                }}
            >
                <strong>Please provide accurate contact details.</strong> Your <strong>email address</strong> and <strong>WhatsApp/phone number</strong> will be used by our team to reach you about this inquiry. Incorrect details may result in delays or a missed response.
            </Alert>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%'}}>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={3} justifyContent="flex-start">
                        {fields.map(field => (
                            <Grid key={field.name} size={{ xs: 12 }}>
                                {field.type === 'phone-with-code' ? (
                                    <PhoneInputField
                                        value={formData[field.name] || ''}
                                        countryCode={formData.phoneCountryCode || 'ZW'}
                                        onChange={(fullNum, dialCode, countryCode) => setFormData(prev => ({
                                            ...prev,
                                            [field.name]: fullNum,
                                            phoneDialCode:    dialCode,
                                            phoneCountryCode: countryCode,
                                        }))}
                                        error={!!errors[field.name]}
                                        helperText={errors[field.name]}
                                        required={field.required}
                                        label={field.label}
                                    />
                                ) : (field.type === 'select' || field.type === 'countrySelect') ? (
                                    <TextField
                                        select
                                        fullWidth
                                        name={field.name}
                                        label={field.label}
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                        required={field.required}
                                        error={!!errors[field.name]}
                                        helperText={errors[field.name]}
                                        variant="outlined"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        SelectProps={{
                                            displayEmpty: true,
                                            renderValue: (selected) => {
                                                if (!selected) {
                                                    return <Box component="em" sx={{ color: 'text.secondary' }}>{field.options.find(opt => opt.value === "")?.label}</Box>;
                                                }
                                                const selectedOption = field.options.find(option => option.value === selected);
                                                return selectedOption ? selectedOption.label : '';
                                            },
                                            MenuProps: {
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 250,
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        {field.options.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        fullWidth type={field.type || 'text'} name={field.name} label={field.label}
                                        value={formData[field.name] || ''} onChange={handleChange} required={field.required}
                                        error={!!errors[field.name]} helperText={errors[field.name]} placeholder={field.placeholder}
                                        multiline={field.type === 'textarea'} rows={field.type === 'textarea' ? 4 : undefined}
                                        InputLabelProps={field.type === 'date' || field.type === 'datetime-local' ? { shrink: true } : {}}
                                    />
                                )}
                            </Grid>
                        ))}
                        {currentPrompt && (
                            <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', my: 1.5, display: 'flex', justifyContent: 'center', width: '100%' }}>
                                <Button variant="outlined" color="success" startIcon={<InfoIcon />}
                                    onClick={() => fetchSuggestiveDataFromAPI(currentPrompt)}
                                    disabled={isLoadingSuggestion}
                                    size="small"
                                >
                                    {isLoadingSuggestion ? "Loading Information..." : "Show Helpful Information"}
                                </Button>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
                            <Collapse in={showHelpfulInfoBox} timeout="auto" sx={{width: '100%'}}>
                                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, borderColor: 'success.main', background: 'success.light', borderRadius: 2 }}>
                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                                        <InfoIcon sx={{mr:1, color: 'success.main' }}/>
                                        <Typography variant="subtitle2" sx={{color: 'success.dark' }} fontWeight="medium">Helpful Information</Typography>
                                    </Box>
                                    {isLoadingSuggestion && <CircularProgress size={18} sx={{mr:1}} color="success" />}
                                    {suggestionError && !isLoadingSuggestion && (<Alert severity="info">{suggestionError}</Alert>)}
                                   <Collapse in={showHelpfulInfoBox} timeout="auto" sx={{width: '100%'}}>
    
    {/* If it's loading, show the spinner */}
    {isLoadingSuggestion && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography variant="body2">Gathering travel tips...</Typography>
        </Box>
    )}

    {/* If there is an error */}
    {suggestionError && !isLoadingSuggestion && (
        <Alert severity="warning" sx={{ mt: 1 }}>{suggestionError}</Alert>
    )}

    {/* 👇 HERE IS YOUR NEW CODE 👇 */}
    {suggestiveText && !isLoadingSuggestion && (
        <div className="suggestion-box">
            <ReactMarkdown>{suggestiveText}</ReactMarkdown>
        </div>
    )}

</Collapse>
                                </Paper>
                            </Collapse>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ mt: 'auto', pt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button variant="outlined" color="secondary" onClick={onBack} disabled={isLoading}>Back</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Proceed to Chat'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};


const commonMuiContactFields = [
    { name: 'fullName', label: 'Full Name', type: 'text', required: true, placeholder: "e.g., John Doe" },
    { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: "e.g., john.doe@example.com" },
    { name: 'phone', label: 'WhatsApp / Phone Number', type: 'phone-with-code' },
];

function travelInquiryMuiFields() {
    return [
        ...commonMuiContactFields,
        { name: 'destination', label: 'Destination', type: 'text', required: true, placeholder: "e.g., Addis Ababa" },
        { name: 'travelDate', label: 'Travel Date', type: 'date', required: true },
        { name: 'travelPurpose', label: 'Purpose of Travel', type: 'select', required: true,
            options: [
                { value: "", label: "Select Purpose" },
                { value: "leisure", label: "Leisure/Vacation" },
                { value: "business", label: "Business" },
                { value: "family", label: "Family Visit" },
                { value: "other", label: "Other" }
            ]
        },
        { name: 'additionalNotes', label: 'Additional Notes', type: 'textarea', placeholder: "Special requests or requirements...", fullWidth: true }
    ];
}

function visaInquiryMuiFields(countries) {
    return [
        ...commonMuiContactFields,
        { name: 'nationality', label: 'Nationality', type: 'countrySelect', required: true,
            options: countries, errorMessage: "Please select your nationality"
        },
        { name: 'destinationCountryVisa', label: 'Destination Country', type: 'countrySelect', required: true,
            options: countries, errorMessage: "Please select destination country"
        },
        { name: 'visaType', label: 'Visa Type', type: 'select', required: true,
            options: [
                { value: "", label: "Select Visa Type" },
                { value: "tourist", label: "Tourist Visa" },
                { value: "business", label: "Business Visa" },
                { value: "transit", label: "Transit Visa" },
                { value: "student", label: "Student Visa" }
            ]
        },
        { name: 'travelDateVisa', label: 'Planned Travel Date', type: 'date', required: true }
    ];
}

function accommodationInquiryMuiFields() {
    return [
        ...commonMuiContactFields,
        { name: 'location', label: 'Location', type: 'text', required: true, placeholder: "e.g., Gondar" },
        { name: 'checkInDate', label: 'Check-in Date', type: 'date', required: true },
        { name: 'checkOutDate', label: 'Check-out Date', type: 'date', required: true },
        { name: 'roomType', label: 'Room Type', type: 'select', required: true,
            options: [
                { value: "", label: "Select Room Type" },
                { value: "single", label: "Single Room" },
                { value: "double", label: "Double Room" },
                { value: "suite", label: "Suite" },
                { value: "family", label: "Family Room" }
            ]
        },
        { name: 'guests', label: 'Number of Guests', type: 'number', required: true, placeholder: "e.g., 2" }
    ];
}

function carHireInquiryMuiFields() {
    return [
        ...commonMuiContactFields,
        { name: 'pickupLocation', label: 'Pickup Location', type: 'text', required: true, placeholder: "e.g., Bole Airport" },
        { name: 'dropoffLocation', label: 'Drop-off Location', type: 'text', placeholder: "Same as pickup if not specified" },
        { name: 'startDate', label: 'Start Date', type: 'date', required: true },
        { name: 'endDate', label: 'End Date', type: 'date', required: true },
        { name: 'carType', label: 'Car Type', type: 'select', required: true,
            options: [
                { value: "", label: "Select Car Type" },
                { value: "economy", label: "Economy" },
                { value: "suv", label: "SUV" },
                { value: "luxury", label: "Luxury" },
                { value: "van", label: "Van" }
            ]
        }
    ];
}

function InquiryDetailsStep({ inquiryType, onSubmit, onBack, isLoading }) {
    const countryList = [
        { value: "", label: "Select Country" }, { value: "AF", label: "Afghanistan" }, { value: "AL", label: "Albania" },
        { value: "DZ", label: "Algeria" }, { value: "AS", label: "American Samoa" }, { value: "AD", label: "Andorra" },
        { value: "AO", label: "Angola" }, { value: "AI", label: "Anguilla" }, { value: "AQ", label: "Antarctica" },
        { value: "AG", label: "Antigua and Barbuda" }, { value: "AR", label: "Argentina" }, { value: "AM", label: "Armenia" },
        { value: "AW", label: "Aruba" }, { value: "AU", label: "Australia" }, { value: "AT", label: "Austria" },
        { value: "AZ", label: "Azerbaijan" }, { value: "BS", label: "Bahamas" }, { value: "BH", label: "Bahrain" },
        { value: "BD", label: "Bangladesh" }, { value: "BB", label: "Barbados" }, { value: "BY", label: "Belarus" },
        { value: "BE", label: "Belgium" }, { value: "BZ", label: "Belize" }, { value: "BJ", label: "Benin" },
        { value: "BM", label: "Bermuda" }, { value: "BT", label: "Bhutan" }, { value: "BO", label: "Bolivia" },
        { value: "BA", label: "Bosnia and Herzegovina" }, { value: "BW", label: "Botswana" }, { value: "BR", label: "Brazil" },
        { value: "IO", label: "British Indian Ocean Territory" }, { value: "BN", label: "Brunei Darussalam" }, { value: "BG", label: "Bulgaria" },
        { value: "BF", label: "Burkina Faso" }, { value: "BI", label: "Burundi" }, { value: "KH", label: "Cambodia" },
        { value: "CM", label: "Cameroon" }, { value: "CA", label: "Canada" }, { value: "CV", label: "Cape Verde" },
        { value: "KY", label: "Cayman Islands" }, { value: "CF", label: "Central African Republic" }, { value: "TD", label: "Chad" },
        { value: "CL", label: "Chile" }, { value: "CN", label: "China" }, { value: "CX", label: "Christmas Island" },
        { value: "CC", label: "Cocos (Keeling) Islands" }, { value: "CO", label: "Colombia" }, { value: "KM", label: "Comoros" },
        { value: "CG", label: "Congo" }, { value: "CD", label: "Congo, The Democratic Republic of The" }, { value: "CK", label: "Cook Islands" },
        { value: "CR", label: "Costa Rica" }, { value: "CI", label: "Cote D'ivoire" }, { value: "HR", label: "Croatia" },
        { value: "CU", label: "Cuba" }, { value: "CY", label: "Cyprus" }, { value: "CZ", label: "Czech Republic" },
        { value: "DK", label: "Denmark" }, { value: "DJ", label: "Djibouti" }, { value: "DM", label: "Dominica" },
        { value: "DO", label: "Dominican Republic" }, { value: "EC", label: "Ecuador" }, { value: "EG", label: "Egypt" },
        { value: "SV", label: "El Salvador" }, { value: "GQ", label: "Equatorial Guinea" }, { value: "ER", label: "Eritrea" },
        { value: "EE", label: "Estonia" }, { value: "ET", label: "Ethiopia" }, { value: "FK", label: "Falkland Islands (Malvinas)" },
        { value: "FO", label: "Faroe Islands" }, { value: "FJ", label: "Fiji" }, { value: "FI", label: "Finland" },
        { value: "FR", label: "France" }, { value: "GF", label: "French Guiana" }, { value: "PF", label: "French Polynesia" },
        { value: "GA", label: "Gabon" }, { value: "GM", label: "Gambia" }, { value: "GE", label: "Georgia" },
        { value: "DE", label: "Germany" }, { value: "GH", label: "Ghana" }, { value: "GI", label: "Gibraltar" },
        { value: "GR", label: "Greece" }, { value: "GL", label: "Greenland" }, { value: "GD", label: "Grenada" },
        { value: "GP", label: "Guadeloupe" }, { value: "GU", label: "Guam" }, { value: "GT", label: "Guatemala" },
        { value: "GN", label: "Guinea" }, { value: "GW", label: "Guinea-bissau" }, { value: "GY", label: "Guyana" },
        { value: "HT", label: "Haiti" }, { value: "VA", label: "Holy See (Vatican City State)" }, { value: "HN", label: "Honduras" },
        { value: "HK", label: "Hong Kong" }, { value: "HU", label: "Hungary" }, { value: "IS", label: "Iceland" },
        { value: "IN", label: "India" }, { value: "ID", label: "Indonesia" }, { value: "IR", label: "Iran, Islamic Republic of" },
        { value: "IQ", label: "Iraq" }, { value: "IE", label: "Ireland" }, { value: "IL", label: "Israel" },
        { value: "IT", label: "Italy" }, { value: "JM", label: "Jamaica" }, { value: "JP", label: "Japan" },
        { value: "JO", label: "Jordan" }, { value: "KZ", label: "Kazakhstan" }, { value: "KE", "label": "Kenya" },
        { value: "KI", label: "Kiribati" }, { value: "KP", label: "Korea, Democratic People's Republic of" }, { value: "KR", label: "Korea, Republic of" },
        { value: "KW", label: "Kuwait" }, { value: "KG", label: "Kyrgyzstan" }, { value: "LA", label: "Lao People's Democratic Republic" },
        { value: "LV", label: "Latvia" }, { value: "LB", label: "Lebanon" }, { value: "LS", label: "Lesotho" },
        { value: "LR", label: "Liberia" }, { value: "LY", label: "Libyan Arab Jamahiriya" }, { value: "LI", label: "Liechtenstein" },
        { value: "LT", label: "Lithuania" }, { value: "LU", label: "Luxembourg" }, { value: "MO", label: "Macao" },
        { value: "MK", label: "Macedonia, The Former Yugoslav Republic of" }, { value: "MG", label: "Madagascar" }, { value: "MW", label: "Malawi" },
        { value: "MY", label: "Malaysia" }, { value: "MV", label: "Maldives" }, { value: "ML", label: "Mali" },
        { value: "MT", label: "Malta" }, { value: "MH", label: "Marshall Islands" }, { value: "MQ", label: "Martinique" },
        { value: "MR", label: "Mauritania" }, { value: "MU", label: "Mauritius" }, { value: "YT", label: "Mayotte" },
        { value: "MX", label: "Mexico" }, { value: "FM", label: "Micronesia, Federated States of" }, { value: "MD", label: "Moldova, Republic of" },
        { value: "MC", label: "Monaco" }, { value: "MN", label: "Mongolia" }, { value: "MS", label: "Montserrat" },
        { value: "MA", label: "Morocco" }, { value: "MZ", label: "Mozambique" }, { value: "MM", label: "Myanmar" },
        { value: "NA", label: "Namibia" }, { value: "NR", label: "Nauru" }, { value: "NP", label: "Nepal" },
        { value: "NL", label: "Netherlands" }, { value: "AN", label: "Netherlands Antilles" }, { value: "NC", label: "New Caledonia" },
        { value: "NZ", label: "New Zealand" }, { value: "NI", label: "Nicaragua" }, { value: "NE", label: "Niger" },
        { value: "NG", label: "Nigeria" }, { value: "NU", label: "Niue" }, { value: "NF", label: "Norfolk Island" },
        { value: "MP", label: "Northern Mariana Islands" }, { value: "NO", label: "Norway" }, { value: "OM", label: "Oman" },
        { value: "PK", label: "Pakistan" }, { value: "PW", label: "Palau" }, { value: "PS", label: "Palestinian Territory, Occupied" },
        { value: "PA", label: "Panama" }, { value: "PG", label: "Papua New Guinea" }, { value: "PY", label: "Paraguay" },
        { value: "PE", label: "Peru" }, { value: "PH", label: "Philippines" }, { value: "PN", label: "Pitcairn" },
        { value: "PL", label: "Poland" }, { value: "PT", label: "Portugal" }, { value: "PR", label: "Puerto Rico" },
        { value: "QA", label: "Qatar" }, { value: "RE", label: "Reunion" }, { value: "RO", label: "Romania" },
        { value: "RU", label: "Russian Federation" }, { value: "RW", "label": "Rwanda" }, { value: "SH", label: "Saint Helena" },
        { value: "KN", label: "Saint Kitts and Nevis" }, { value: "LC", label: "Saint Lucia" }, { value: "PM", label: "Saint Pierre and Miquelon" },
        { value: "VC", label: "Saint Vincent and The Grenadines" }, { value: "WS", label: "Samoa" }, { value: "SM", label: "San Marino" },
        { value: "ST", label: "Sao Tome and Principe" }, { value: "SA", label: "Saudi Arabia" }, { value: "SN", label: "Senegal" },
        { value: "CS", label: "Serbia and Montenegro" }, { value: "SC", label: "Seychelles" }, { value: "SL", label: "Sierra Leone" },
        { value: "SG", label: "Singapore" }, { value: "SK", label: "Slovakia" }, { value: "SI", label: "Slovenia" },
        { value: "SB", label: "Solomon Islands" }, { value: "SO", label: "Somalia" }, { value: "ZA", label: "South Africa" },
        { value: "GS", label: "South Georgia and The South Sandwich Islands" }, { value: "ES", label: "Spain" }, { value: "LK", label: "Sri Lanka" },
        { value: "SD", label: "Sudan" }, { value: "SR", label: "Suriname" }, { value: "SJ", label: "Svalbard and Jan Mayen" },
        { value: "SZ", label: "Swaziland" }, { value: "SE", label: "Sweden" }, { value: "CH", label: "Switzerland" },
        { value: "SY", label: "Syrian Arab Republic" }, { value: "TW", label: "Taiwan, Province of China" }, { value: "TJ", label: "Tajikistan" },
        { value: "TZ", label: "Tanzania, United Republic of" }, { value: "TH", label: "Thailand" }, { value: "TL", label: "Timor-leste" },
        { value: "TG", label: "Togo" }, { value: "TK", label: "Tokelau" }, { value: "TO", label: "Tonga" },
        { value: "TT", label: "Trinidad and Tobago" }, { value: "TN", label: "Tunisia" }, { value: "TR", label: "Turkey" },
        { value: "TM", label: "Turkmenistan" }, { value: "TC", label: "Turks and Caicos Islands" }, { value: "TV", label: "Tuvalu" },
        { value: "UG", label: "Uganda" }, { value: "UA", label: "Ukraine" }, { value: "AE", label: "United Arab Emirates" },
        { value: "GB", label: "United Kingdom" }, { value: "US", label: "United States" }, { value: "UM", label: "United States Minor Outlying Islands" },
        { value: "UY", label: "Uruguay" }, { value: "UZ", label: "Uzbekistan" }, { value: "VU", label: "Vanuatu" },
        { value: "VE", label: "Venezuela" }, { value: "VN", label: "Viet Nam" }, { value: "VG", label: "Virgin Islands, British" },
        { value: "VI", label: "Virgin Islands, U.S." }, { value: "WF", label: "Wallis and Futuna" }, { value: "EH", label: "Western Sahara" },
        { value: "YE", label: "Yemen" }, { value: "ZM", label: "Zambia" }, { value: "ZW", label: "Zimbabwe" }
    ];

    const getCountryLabel = (code) => {
        const country = countryList.find(c => c.value === code);
        return country ? country.label : code;
    };

    let fields = [];
    let title = "Provide Your Inquiry Details";
    switch (inquiryType) {
        case 'travelInquiry': fields = travelInquiryMuiFields(); title = "Travel Inquiry Details"; break;
        case 'visaInquiry': fields = visaInquiryMuiFields(countryList); title = "Visa Inquiry Details"; break;
        case 'accommodationInquiry': fields = accommodationInquiryMuiFields(); title = "Accommodation Details"; break;
        case 'carHireInquiry': fields = carHireInquiryMuiFields(); title = "Car Hire Details"; break;
        default:
            fields = commonMuiContactFields; title = "General Inquiry Details";
    }
    
    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start', 
            width: '100%',
            flexGrow: 1,
            // MODIFIED: Remove side padding on extra-small screens
            p: { xs: 0, sm: 1 } 
        }}>
            <Paper elevation={3} sx={{
             width: '100%',
             maxWidth: '800px',
             borderRadius: { xs: 0, sm: 3 },
             p: { xs: 1.5, sm: 3, md: 4 },
             boxShadow: { xs: 'none', sm: '0 10px 40px rgba(0,0,0,0.10)' },
             borderTop: { sm: '4px solid' },
             borderColor: { sm: 'primary.main' },
             overflowY: 'auto',
             '&::-webkit-scrollbar': { display: 'none' }
             }}>
                <BaseInquiryFormMui 
                    title={title} 
                    fields={fields} 
                    onSubmit={onSubmit} 
                    onBack={onBack}
                    isLoading={isLoading} 
                    inquiryType={inquiryType}
                    getCountryLabel={getCountryLabel}
                />
            </Paper>
        </Box>
    );
}